import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  Badge,
  badgeStyle,
  badgeType,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  iconBtnType,
  IconButton,
  Icons,
} from '../../../../../packages';

import { useDomainType } from '../../../../../hooks/useDomainType';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { createNewAssociatedTag } from '../../../../../services/member/member.service';
import { DomainTypesEnum } from '../../../../../constants/CommonConstants';
import { getAuth } from '../../../../../redux/modules/auth/selectors';

import AddNewReport from '../AddNewReport';
import PriorityEmpty from '../PriorityEmpty';
import ViewDetail from '../SideEffects/components/side-effect-detail';
import EditItem from '../SideEffects/components/side-effect-editing';
import SideEffectCard from '../SideEffects/components/side-effect-card';

import { stepTitles } from './SocialDeterminants.constants';
// styles
import { useStyles } from '../../MemberDetail.styles';

const SocialDeterminants = () => {
  const classes = useStyles({});
  const dispatch = useDispatch();
  const { memberId } = useParams();
  const [isLoading, domainData, domainOptions] = useDomainType({
    type: DomainTypesEnum.SOCIALS_DETERMINANTS,
  });
  const { meta } = useSelector(getAuth);
  const { profile, selectedPatient } = useSelector(getProfile);
  const [isSaving, setIsSaving] = useState(false);
  const [errorApi, setError] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const onAddNewSocialDeterminants = () => {
    setShowAddItem(true);
  };

  const onSelectedItemClick = (item, isEdit = false) => {
    setSelectedItem(item);
    setIsEditing(isEdit);
  };

  const onCloseDrawer = () => {
    setSelectedItem(null);
    setIsEditing(false);
    setShowAddItem(false);
  };

  const onClickNewReport = () => {
    setShowAddItem(true);
  };

  const onUpdateItem = async data => {
    setIsSaving(true);
    setError(null);
    try {
      await createNewAssociatedTag({
        patientId: memberId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: selectedItem.domainElementId,
            metaData: {
              notes: data.notes,
              interferenceInLife: data.isInterference,
              selectedImportance: data.status,
            },
          },
        ],
      });
      setIsSaving(false);
      dispatch(profileActionCreators.fetchDomainTypes(memberId));
      onCloseDrawer();
    } catch (error) {
      setError(error.data.message || error.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const onSubmitNewReport = payload => {
    const data = {
      patientId: memberId,
      tagItems: payload.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          interferenceInLife: item.isInterference,
          notes: item.notes,
          selectedImportance: item.status,
        },
      })),
    };
    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: errorMsg => {
          if (!errorMsg) {
            onCloseDrawer();
          }
        },
      }),
    );
  };

  return (
    <>
      <ViewDetail
        open={!!selectedItem && !isEditing}
        onClose={onCloseDrawer}
        onClickNewReport={onClickNewReport}
        item={selectedItem}
      />
      <EditItem
        title="Update Social Determinant"
        isOpen={!!selectedItem && isEditing}
        onClose={onCloseDrawer}
        item={selectedItem}
        onSubmit={onUpdateItem}
        isSaving={isSaving}
        error={errorApi}
      />
      <AddNewReport
        isOpen={showAddItem}
        onClose={onCloseDrawer}
        title="Report new social determinant"
        stepTitles={stepTitles}
        showInterference={false}
        searchLabel="Select one or more social determinants"
        options={domainOptions}
        onSubmit={onSubmitNewReport}
      />
      <div className={classes.headingWithBtnWrapper}>
        <div className={classes.noteHeaderLeftSide}>
          <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
            Social determinants
          </Heading>
          <Badge variant={badgeType.OUTLINED} style={badgeStyle.UNRELATED} className={classes.totalBadge}>
            {domainData?.relatedElements.length || 0} total
          </Badge>
        </div>
        {domainData?.relatedElements.length > 0 && (
          <IconButton
            className={classes.addBtn}
            icon="plus"
            variant={iconBtnType.PRIMARY}
            onClick={onAddNewSocialDeterminants}
          >
            Add social determinant
          </IconButton>
        )}
      </div>
      <div className={classes.noteCardWrapper}>
        {isLoading ? (
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        ) : (
          <>
            {domainData?.relatedElements.length ? (
              <>
                {domainData.relatedElements.map((item, idx) => (
                  <SideEffectCard item={item} key={idx} onClick={onSelectedItemClick} />
                ))}
              </>
            ) : (
              <PriorityEmpty
                title={`No Social Determinants reported for ${selectedPatient?.member.nickName} yet.`}
                // eslint-disable-next-line max-len
                description="If you've spoken with them about their personal situation, make sure you save that information here."
                addText="Add social determinant"
                onAdd={onAddNewSocialDeterminants}
                glyph="dinner"
                glyphOrientation="landscape"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export { SocialDeterminants };
